import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiChevronDown, FiArrowRight } from 'react-icons/fi';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_arvaron-refresh/artifacts/mdrasy8y_17.png';

const businessVerticals = [
  { name: 'Arvaron Sourcing', path: '/business/sourcing' },
  { name: 'Arvaron Logistics', path: '/business/logistics' },
  { name: 'Arvaron Exports', path: '/business/exports' },
  { name: 'Arvaron Technologies', path: '/business/technologies' },
  { name: 'Arvaron Digital', path: '/business/digital' },
  { name: 'Arvaron Infra', path: '/business/infra' },
  { name: 'Arvaron Retail', path: '/business/retail' },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBusinessesOpen, setIsBusinessesOpen] = useState(false);
  const [isMobileBusinessesOpen, setIsMobileBusinessesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsBusinessesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsBusinessesOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Businesses', path: '/businesses', hasDropdown: true },
    { name: 'Careers', path: '/careers' },
    { name: 'Blog', path: '/blog' },
  ];

  const linkClass = (path) =>
    `text-[15px] tracking-[0.05em] font-medium transition-all duration-300 text-white ${
      location.pathname === path || (path === '/businesses' && location.pathname.startsWith('/business'))
        ? 'opacity-100'
        : 'opacity-70 hover:opacity-100'
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 backdrop-blur-xl ${
        isScrolled
          ? 'shadow-[0_4px_30px_rgba(0,0,0,0.2)] border-b border-white/5'
          : ''
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-28">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img
              src={LOGO_URL}
              alt="Arvaron Group"
              className="h-[84px] md:h-[104px] w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-9">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div key={link.path} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsBusinessesOpen(!isBusinessesOpen)}
                    onMouseEnter={() => setIsBusinessesOpen(true)}
                    data-testid="nav-link-businesses"
                    className={`${linkClass(link.path)} flex items-center`}
                  >
                    {link.name}
                    <FiChevronDown className={`ml-1 transition-transform duration-200 ${isBusinessesOpen ? 'rotate-180' : ''}`} size={14} />
                  </button>

                  {isBusinessesOpen && (
                    <div
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-64 bg-[#0a0a0a] border border-white/10 rounded-lg shadow-2xl overflow-hidden"
                      onMouseLeave={() => setIsBusinessesOpen(false)}
                      data-testid="businesses-dropdown"
                    >
                      <Link
                        to="/businesses"
                        className="block px-5 py-3 text-[12px] tracking-wider text-[#17B9BE] hover:bg-white/5 transition-colors border-b border-white/5 font-semibold"
                      >
                        View All Verticals
                      </Link>
                      {businessVerticals.map((v) => (
                        <Link
                          key={v.path}
                          to={v.path}
                          className={`block px-5 py-3 text-[13px] transition-colors ${
                            location.pathname === v.path
                              ? 'text-[#17B9BE] bg-white/5'
                              : 'text-white/60 hover:text-white hover:bg-white/5'
                          }`}
                        >
                          {v.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className={linkClass(link.path)}
                >
                  {link.name}
                </Link>
              )
            )}

            {/* CTA Button */}
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="ml-2 group px-7 py-3 brand-gradient-bg text-black text-[15px] font-semibold rounded-sm hover:opacity-90 transition-all inline-flex items-center"
            >
              Get in Touch
              <FiArrowRight className="ml-2 group-hover:translate-x-0.5 transition-transform" size={16} />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white text-2xl"
          >
            {isMobileMenuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden pb-6 border-t border-white/10 mt-2" data-testid="mobile-menu">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div key={link.path}>
                  <button
                    onClick={() => setIsMobileBusinessesOpen(!isMobileBusinessesOpen)}
                    className={`w-full text-left flex items-center justify-between py-3 text-[13px] tracking-[0.05em] font-medium transition-colors ${
                      location.pathname.startsWith('/business') ? 'text-white' : 'text-white/50'
                    }`}
                  >
                    {link.name}
                    <FiChevronDown className={`transition-transform duration-200 ${isMobileBusinessesOpen ? 'rotate-180' : ''}`} size={14} />
                  </button>
                  {isMobileBusinessesOpen && (
                    <div className="pl-4 border-l border-white/10 ml-2 mb-2">
                      <Link
                        to="/businesses"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block py-2 text-[12px] text-[#17B9BE]"
                      >
                        View All
                      </Link>
                      {businessVerticals.map((v) => (
                        <Link
                          key={v.path}
                          to={v.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`block py-2 text-[13px] ${
                            location.pathname === v.path ? 'text-[#17B9BE]' : 'text-white/40 hover:text-white'
                          }`}
                        >
                          {v.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-3 text-[13px] tracking-[0.05em] font-medium transition-colors ${
                    location.pathname === link.path ? 'text-white' : 'text-white/50 hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
            {/* Mobile CTA */}
            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="mobile-cta-button"
              className="mt-4 block text-center px-6 py-3 brand-gradient-bg text-black text-[13px] font-semibold rounded-sm"
            >
              Get in Touch
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
